import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { FormValues } from '../../../types/ChampionApplicationFormTypes';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import { CountrySearchField } from '../CountrySearchField';
import { BirthDateField } from '../BirthDateField';

type FieldErrors = {
  street?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  birthDate?: string;
};

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  errors?: FieldErrors;
};

export const AddressSection: React.FC<Props> = ({ values, updateField, errors }) => {
  return (
    <div css={sectionBoxStyles}>
      <div css={sectionTitleStyles}>Address</div>

      <div css={fieldStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="street">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Street address
          <FieldHelpTooltip
            content="Please enter a street address; PO box is not accepted."
            iconLabel="Street address Help Info"
          />
        </label>
        <Textfield
          id="street"
          value={values.street}
          onChange={(e) => updateField('street', (e.target as HTMLInputElement).value)}
          isInvalid={!!errors?.street}
          isCompact
          maxLength={50}
        />
        {errors?.street && <div css={errorStyles}>{errors.street}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="city">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          City
        </label>
        <Textfield
          id="city"
          value={values.city}
          onChange={(e) => updateField('city', (e.target as HTMLInputElement).value)}
          isInvalid={!!errors?.city}
          isCompact
          maxLength={80}
        />
        {errors?.city && <div css={errorStyles}>{errors.city}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="state-province">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          State/province
        </label>
        <Textfield
          id="state-province"
          value={values.stateProvince}
          onChange={(e) => updateField('stateProvince', (e.target as HTMLInputElement).value)}
          isInvalid={!!errors?.stateProvince}
          isCompact
          maxLength={20}
        />
        {errors?.stateProvince && <div css={errorStyles}>{errors.stateProvince}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="postal-code">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Zip/post code
        </label>
        <Textfield
          id="postal-code"
          value={values.postalCode}
          onChange={(e) => updateField('postalCode', (e.target as HTMLInputElement).value)}
          isInvalid={!!errors?.postalCode}
          isCompact
          maxLength={10}
        />
        {errors?.postalCode && <div css={errorStyles}>{errors.postalCode}</div>}
      </div>

      <div css={fieldTopStyles}>
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="country">
          <abbr css={requiredStyles} title="required" aria-hidden="true">
            *
          </abbr>
          Country
          <FieldHelpTooltip
            content="Search for and select your Country from the look up list below."
            iconLabel="Country Help Info"
          />
        </label>
        <CountrySearchField
          inputId="country"
          value={values.country}
          onChange={(v) => updateField('country', v)}
          isInvalid={!!errors?.country}
        />
        {errors?.country && <div css={errorStyles}>{errors.country}</div>}
      </div>

      <div css={fieldTopStyles} className="champion-form-birth-date">
        <label css={[labelStyles, salesforceLabelStyles]} htmlFor="birth-date">
          Birth date
          <FieldHelpTooltip
            content="Please enter date in this format: MM/DD/YYYY."
            iconLabel="Birth date Help Info"
          />
        </label>
        <BirthDateField
          id="birth-date"
          value={values.birthDate}
          onChange={(v) => updateField('birthDate', v)}
          isInvalid={!!errors?.birthDate}
        />
        {errors?.birthDate && <div css={errorStyles}>{errors.birthDate}</div>}
      </div>
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const sectionTitleStyles = css({
  display: 'none',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '12px',
  letterSpacing: '0.04em',
  marginBottom: token('space.100'),
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '40px',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.875rem',
  color: '#3e3e3c',
  marginBottom: token('space.050'),
  lineHeight: 1.5,
});

// Form label: smaller, normal weight, tighter spacing
const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
